const origin = window.location.origin;
const defaultUrl = document.querySelector('[data-resource-center-data-page]').getAttribute('data-resource-center-data-page');
const FETCH_URL = `${origin}${defaultUrl}?view=resources-data-template`;

/**
 * ResourceFilter - A comprehensive resource filtering, sorting, and pagination system
 * 
 * @class ResourceFilter
 * @description Manages dynamic resource display with filtering by user type and topic,
 * sorting capabilities, and paginated results. Fully accessible with WCAG 2.1 AA compliance.
 * 
 * Features:
 * - Dynamic resource loading via fetch API
 * - Multi-criteria filtering (user_type, topic)
 * - Multiple sorting options (A-Z, Z-A, date-desc)
 * - Pagination with configurable items per page
 * - Smooth animations and transitions
 * - Full keyboard navigation support
 * - Screen reader accessibility
 * - Responsive design for mobile and desktop
 * 
 * @example
 * // Initialize automatically on DOM ready
 * const resourceFilter = new ResourceFilter();
 * 
 * // Or access the global instance
 * window.resourceFilter.applyFilters();
 * 
 * @version 1.0.0
 * @author Symmetry Commerce
 * @since 2024
 */
class ResourceFilter {
  /**
   * Creates a new ResourceFilter instance
   * @constructor
   * @description Initializes the resource filter system with default settings
   * and begins the initialization process
   */
  constructor() {
    /** @type {Array} Original unfiltered resource data */
    this.originalResources = [];
    
    /** @type {Array} Currently filtered resource data */
    this.filteredResources = [];
    
    /** @type {boolean} Whether filtering functionality is enabled */
    this.filtersEnabled = true;
    
    /** @type {boolean} Whether sorting functionality is enabled */
    this.sortingEnabled = true;

    /** @type {boolean} Whether filters are currently applied */
    this.filtersApplied = false;
    
    /** @type {Object} Current filter selections */
    this.currentFilters = {
      user_type: '',
      topic: ''
    };
    this.filterElements = document.querySelectorAll('[data-filter-type]');
    this.originalFilterData = {
      user_type: [],
      topic: []
    };
    
    /** @type {string} Current sort method */
    this.currentSort = 'none';
    
    /** @type {number} Current page number (1-based) */
    this.currentPage = 1;
    
    /** @type {number} Number of resources to display per page */
    this.resourcesPerPage = 16;
    
    /** @type {number} Total number of pages */
    this.totalPages = 1;
    
    /** @type {HTMLElement|null} Pagination controls container */
    this.paginationContainer = null;

    //console.log('ResourceFilter initializing...');
    this.init();
  }

  /**
   * Initializes the ResourceFilter system
   * @async
   * @description Orchestrates the complete initialization process including data loading,
   * pagination setup, event binding, and initial rendering
   * @returns {Promise<void>} Resolves when initialization is complete
   */
  async init() {
    await this.loadResourceData();
    this.loadFilterData();
    this.initializePagination();
    this.bindFilterEvents();
    this.bindSortEvents();
    this.renderResources();
    //console.log('ResourceFilter initialized successfully');
  }

  loadFilterData(resources = this.originalResources) {
    this.filterElements.forEach(select => {
      let filterType = select.getAttribute('data-filter-type');
      this.originalFilterData[filterType] = Array.from(select.querySelectorAll('option')).map(option => option.value);

      if(filterType === 'user_type') {
        filterType = 'user_type_text';
      }

      const uniqueOptions = [...new Set(resources.map(resource => resource[filterType]))];

      this.createFilterOptions(select, uniqueOptions);
    });
  }

  createFilterOptions(select, uniqueOptions) {
    select.innerHTML = '';

    if(uniqueOptions.length === 0) {
      return;
    }

    const firstOption = document.createElement('option');
    firstOption.value = '';
    firstOption.textContent = select.getAttribute('data-filter-placeholder');
    select.appendChild(firstOption);
    
    uniqueOptions.forEach(option => {
      const newOption = document.createElement('option');
      newOption.value = option;
      newOption.textContent = option;
      select.appendChild(newOption);
    });
  }

  /**
   * Loads resource data from the API endpoint
   * @async
   * @description Fetches resource data and stores it in originalResources.
   * Handles errors gracefully by setting empty array on failure.
   * @returns {Promise<void>} Resolves when data loading is complete
   */
  async loadResourceData() {
    try {
      this.originalResources = await this.fetchResourceData() || [];
      //console.log('Loaded resources:', this.originalResources.length);
    } catch (error) {
      console.error('Error fetching resource data:', error);
      this.originalResources = [];
    }
  }

  /**
   * Fetches resource data from the server
   * @async
   * @private
   * @description Makes HTTP request to the resources data endpoint and returns parsed JSON
   * @returns {Promise<Array>} Array of resource objects, or empty array on error
   * @throws {Error} HTTP errors or network failures
   */
  async fetchResourceData() {
    try {
      //console.log('Fetching resource data from endpoint...');
      const response = await fetch(FETCH_URL);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      // console.log('Resource data:', data);
      return data.resources || [];
    } catch (error) {
      console.error('Error fetching resource data:', error);
      this.originalResources = [];
    }
  }

  /**
   * Binds event listeners to filter controls
   * @description Finds all filter select elements and attaches change event listeners.
   * Also adds accessibility attributes if not already present.
   * Disables filtering if no filter elements are found.
   * @returns {void}
   */
  bindFilterEvents() {
    const filterSelects = document.querySelectorAll('[data-filter-type]');

    if(filterSelects.length === 0) {
      this.filtersEnabled = false;
      //console.log('No filter selects found, disabling filters');
      return;
    }

    //console.log('Found filter selects:', filterSelects.length);
    
    filterSelects.forEach(select => {
      //console.log('Binding event to:', select.id, 'type:', select.getAttribute('data-filter-type'));
      
      // Add accessibility attributes
      if (!select.getAttribute('aria-label') && !select.getAttribute('aria-labelledby')) {
        const filterType = select.getAttribute('data-filter-type');
        select.setAttribute('aria-label', `Filter by ${filterType.replace('_', ' ')}`);
      }
      
      select.addEventListener('change', (e) => {
        const filterType = e.target.getAttribute('data-filter-type');
        const filterValue = e.target.value;
        
        //console.log('Filter changed:', filterType, '=', filterValue);
        this.currentFilters[filterType] = filterValue;
        this.applyFilters();
      });
    });
  }

  /**
   * Binds event listeners to sort controls
   * @description Finds the sort select element and attaches change event listener.
   * Also adds accessibility attributes if not already present.
   * Disables sorting if no sort element is found.
   * @returns {void}
   */
  bindSortEvents() {    
    const sortSelect = document.querySelector('[data-sort-type="sort_by"]');

    if(!sortSelect) {
        this.sortingEnabled = false;
        //console.log('No sort select found, disabling sorting');
        return;
    }

    //console.log('Found Sort select: ', sortSelect);

    // Add accessibility attributes
    if (!sortSelect.getAttribute('aria-label') && !sortSelect.getAttribute('aria-labelledby')) {
      sortSelect.setAttribute('aria-label', 'Sort resources');
    }

    sortSelect.addEventListener('change', (e) => {
        const sortValue = e.target.value;
        //console.log('Sort value changed: ', sortValue);

        this.currentSort = sortValue;
        //console.log('Filtered Applied?: ', this.filtersApplied);
        //console.log('this.filteredResources:', this.filteredResources);
        const sortedResources = this.applySort(this.filtersApplied ? this.filteredResources : this.originalResources);

        this.renderResources(sortedResources);
    })
  }

  /**
   * Applies current filter settings to the resource list
   * @description Filters resources based on user_type and topic selections.
   * If no filters are applied, shows all resources. Automatically applies
   * current sorting and re-renders the display.
   * @returns {void}
   */
  applyFilters() {
    if(this.currentFilters.user_type === '' && this.currentFilters.topic === '') {
      if(this.currentSort !== 'none') {
        const sortedResources = this.applySort(this.originalResources);
        this.renderResources(sortedResources);
      } else {
        this.renderResources(this.originalResources);
      }

      this.filtersApplied = false;
      return;
    }

    //console.log('Applying filters:', this.currentFilters);
    let filteredResources = [...this.originalResources];

    // Apply user type filter
    if (this.currentFilters.user_type !== '') {
      filteredResources = filteredResources.filter(resource => 
        resource.user_type_text === this.currentFilters.user_type
      );
      //console.log('After user_type filter:', filteredResources.length);
      this.filtersApplied = true;
    }

    // Apply topic filter
    if (this.currentFilters.topic !== '') {
      filteredResources = filteredResources.filter(resource => 
        resource.topic === this.currentFilters.topic
      );
      //console.log('After topic filter:', filteredResources.length);
      this.filtersApplied = true;
    }

    //console.log('Final filtered resources:', filteredResources.length);
    this.filteredResources = filteredResources;
    
    if(this.currentSort !== 'none') {
      const sortedResources = this.applySort(filteredResources);
      this.renderResources(sortedResources);
    } else {
      this.renderResources(filteredResources);
    }
  }

  /**
   * Applies sorting to a collection of resources
   * @param {Array} resources - Array of resource objects to sort
   * @description Sorts resources based on currentSort setting. Supports:
   * - 'a-z': Alphabetical ascending by name
   * - 'z-a': Alphabetical descending by name  
   * - 'date-desc': Date ascending (oldest first)
   * - 'none': No sorting applied
   * @returns {Array} New sorted array of resources
   * @example
   * const sorted = this.applySort(this.originalResources);
   */
  applySort(resources) {
    //console.log('Applying sort:', this.currentSort);
    let sortedResources = [...resources];
    
    if(this.currentSort === 'none') {
        return resources;
    }

    switch(this.currentSort.toLowerCase()) {
        case 'a-z':
            //console.log('Sorting by a-z');
            sortedResources.sort((a, b) => {
                //console.log('a:', a.name);
                //console.log('b:', b.name);
                return a.name.localeCompare(b.name);
            });
            break;
        case 'z-a':
            //console.log('Sorting by z-a');
            sortedResources.sort((a, b) => {
                //console.log('a:', a.name);
                //console.log('b:', b.name);
                return b.name.localeCompare(a.name);
            });
            break;
        case 'date-desc':
          //console.log('Sorting by oldest');
            sortedResources.sort((a, b) => {
              // If both dates exist, compare them
              if(this.isDate(a.date) && this.isDate(b.date)) {
                return new Date(a.date) - new Date(b.date);
              }
              // If only a has a date, it should come first
              if(this.isDate(a.date)) {
                //console.log('a has a date, it should come first', a);
                return -1;
              }
              // If only b has a date, it should come first
              if(this.isDate(b.date)) {
                //console.log('b has a date, it should come first', b);
                return 1;
              }
              // If neither has a date, maintain original order
              return 0;
            })
            break;
        default:
            //console.log('Unknown sort type selected: ', this.currentSort);
            break;
    }

    //console.log('Sorted resources:', sortedResources);
    return sortedResources;
  }

  /**
   * Renders a specific page of resources
   * @param {Array} resources - Array of all resources to paginate
   * @param {number} pageNumber - Page number to render (1-based)
   * @description Calculates the subset of resources for the given page,
   * generates HTML for each resource card, and updates the grid container.
   * Also triggers entrance animations for the new cards.
   * @returns {void}
   */
  renderPage(resources, pageNumber) {
    const startIndex = (pageNumber - 1) * this.resourcesPerPage;
    const endIndex = startIndex + this.resourcesPerPage;
    const pageResources = resources.slice(startIndex, endIndex);

    //console.log(`Rendering page ${pageNumber}: showing ${pageResources.length} resources`);

    const resourceCards = pageResources.map((resource, index) => {
      return this.createResourceCardHTML(resource, startIndex + index + 1);
    }).join('');

    this.gridContainer.innerHTML = resourceCards;
    
    // Trigger animations for the cards
    this.triggerFilteredCardAnimations();
  }

  /**
   * Updates the pagination controls based on current state
   * @param {number} totalResources - Total number of resources to paginate
   * @description Generates accessible pagination HTML with Previous/Next buttons,
   * page numbers, and ellipsis for large page counts. Includes full ARIA support
   * for screen readers. Hides pagination if only one page exists.
   * @returns {void}
   */
  updatePagination(totalResources) {
    this.totalPages = Math.ceil(totalResources / this.resourcesPerPage);
    
    if (!this.paginationContainer) {
      console.error('Pagination container not found');
      return;
    }

    if (this.totalPages <= 1) {
      this.paginationContainer.innerHTML = '';
      return;
    }

    let paginationHTML = '<nav class="pagination" aria-label="Resource pagination" role="navigation">';
    
    // Previous button
    if (this.currentPage > 1) {
      paginationHTML += `<button class="pagination__btn pagination__btn--prev" data-page="${this.currentPage - 1}" aria-label="Go to previous page, page ${this.currentPage - 1}">‹ Previous</button>`;
    }

    // Page numbers
    const startPage = Math.max(1, this.currentPage - 2);
    const endPage = Math.min(this.totalPages, this.currentPage + 2);

    if (startPage > 1) {
      paginationHTML += `<button class="pagination__btn" data-page="1" aria-label="Go to page 1">1</button>`;
      if (startPage > 2) {
        paginationHTML += `<span class="pagination__dots" aria-hidden="true">...</span>`;
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      const isCurrentPage = i === this.currentPage;
      if (isCurrentPage) {
        paginationHTML += `<button class="pagination__btn pagination__btn--current" data-page="${i}" aria-current="page" aria-label="Current page, page ${i}">${i}</button>`;
      } else {
        paginationHTML += `<button class="pagination__btn" data-page="${i}" aria-label="Go to page ${i}">${i}</button>`;
      }
    }

    if (endPage < this.totalPages) {
      if (endPage < this.totalPages - 1) {
        paginationHTML += `<span class="pagination__dots" aria-hidden="true">...</span>`;
      }
      paginationHTML += `<button class="pagination__btn" data-page="${this.totalPages}" aria-label="Go to page ${this.totalPages}">${this.totalPages}</button>`;
    }

    // Next button
    if (this.currentPage < this.totalPages) {
      paginationHTML += `<button class="pagination__btn pagination__btn--next" data-page="${this.currentPage + 1}" aria-label="Go to next page, page ${this.currentPage + 1}">Next ›</button>`;
    }

    paginationHTML += '</nav>';

    this.paginationContainer.innerHTML = paginationHTML;
    this.bindPaginationEvents();
  }

  /**
   * Binds event listeners to pagination buttons
   * @private
   * @description Adds click and keyboard event listeners to all pagination buttons.
   * Handles page navigation, filter/sort preservation, smooth scrolling, and
   * focus management for accessibility.
   * @returns {void}
   */
  bindPaginationEvents() {
    const paginationButtons = this.paginationContainer.querySelectorAll('.pagination__btn');
    
    paginationButtons.forEach(button => {
      // Add keyboard navigation
      button.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          button.click();
        }
      });

      button.addEventListener('click', (e) => {
        const page = parseInt(e.target.getAttribute('data-page'));
        if (page && page !== this.currentPage) {
          const previousPage = this.currentPage;
          this.currentPage = page;
          
          // Re-render with current filters and sorting
          let resources = [...this.originalResources];
          
          // Apply filters
          if (this.currentFilters.user_type !== '') {
            resources = resources.filter(resource => 
              resource.user_type_text === this.currentFilters.user_type
            );
          }
          if (this.currentFilters.topic !== '') {
            resources = resources.filter(resource => 
              resource.topic === this.currentFilters.topic
            );
          }
          
          // Apply sorting
          resources = this.applySort(resources);
          
          this.renderPage(resources, this.currentPage);
          this.updatePagination(resources.length);
          
          // Scroll to top of grid
          this.gridContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
          
          // Manage focus - set focus to the current page button after re-render
          setTimeout(() => {
            const currentPageButton = this.paginationContainer.querySelector(`[data-page="${this.currentPage}"][aria-current="page"]`);
            if (currentPageButton) {
              currentPageButton.focus();
            }
          }, 150);
        }
      });
    });
  }

  /**
   * Renders the complete resource list with pagination
   * @param {Array} resources - Array of resources to render
   * @description Main rendering method that handles empty states, pagination setup,
   * and delegates to renderPage for actual card rendering. Resets to page 1
   * when new resources are provided.
   * @returns {void}
   */
  renderResources(resources = this.originalResources) {
    //console.log('Rendering resources with pagination:', resources.length);
    
    if (!this.gridContainer) {
      console.error('Grid container not available for rendering');
      console.error('Attempted to find: #ResourceGrid .resource-grid--wrapper');
      console.error('Available containers:', document.querySelectorAll('#ResourceGrid *'));
      return;
    }

    if (resources.length === 0) {
      //console.log('No resources match filters, showing empty message');
      this.gridContainer.innerHTML = `
        <div class="grid__item">
          <p role="status">No resources found matching your filters.</p>
        </div>
      `;
      this.updatePagination(0);
      return;
    }

    // Reset to first page when filters change
    this.currentPage = 1;
    
    this.updatePagination(resources.length);
    this.renderPage(resources, this.currentPage);
  }

  /**
   * Triggers staggered entrance animations for resource cards
   * @private
   * @description Animates cards from opacity 0 to 1 and transforms them from
   * translateY(20px) to translateY(0) with a 50ms stagger delay between cards.
   * Creates a smooth, professional loading animation.
   * @returns {void}
   */
  triggerFilteredCardAnimations() {
    // For filtered cards, use our custom animation
    const newCards = this.gridContainer.querySelectorAll('.grid__item');
    
    newCards.forEach((card, index) => {
      // Add animation with a small delay for staggered effect
      setTimeout(() => {
        card.style.opacity = '1';
        card.style.transform = 'translateY(0)';
        card.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
      }, index * 50); // 50ms delay between each card
    });
  }

  /**
   * Generates HTML for a single resource card
   * @param {Object} resource - Resource object containing name, image, url, etc.
   * @param {number} index - Unique index for accessibility IDs
   * @description Creates accessible HTML structure for a resource card with:
   * - Semantic article element with proper ARIA labeling
   * - Responsive image handling with lazy loading
   * - Placeholder fallback for missing images
   * - Screen reader optimized structure
   * - Animation-ready initial state (opacity: 0, transform: translateY(20px))
   * @returns {string} HTML string for the resource card
   * @example
   * const cardHTML = this.createResourceCardHTML({
   *   name: 'Sample Resource',
   *   image: '/path/to/image.jpg',
   *   url: '/resource-page'
   * }, 1);
   */
  createResourceCardHTML(resource, index) {
    const hasImage = resource.image && resource.image !== '';
    const resourceName = resource.name || 'Untitled Resource';
    const resourceUrl = resource.url || '#';
    
    return `
      <div class="grid__item" style="opacity: 0; transform: translateY(20px);">
        <article class="grid-view-item resource-card text-align--left" data-resource-url="${resourceUrl}" role="article" aria-labelledby="resource-title-${index}">
          <div>
            <div class="grid-view-item-image">
              <a href="${resourceUrl}" 
                 title="View ${resourceName}" 
                 class="grid__image grid__image__match" 
                 tabindex="-1" 
                 data-resource-card-link
                 aria-label="View ${resourceName}">
                ${hasImage ? `
                  <img src="${resource.image}" 
                       alt="${resourceName}" 
                       loading="lazy" 
                       data-resource-card-image />
                ` : `
                  <div class="icon icon--placeholder grid__image" 
                       style="width:100%; height:200px; background:#f0f0f0; display:flex; align-items:center; justify-content:center; color:#999;"
                       role="img"
                       aria-label="No image available for ${resourceName}">
                    <span aria-hidden="true">No Image</span>
                  </div>
                `}
              </a>
            </div>
            
            <div class="resource-grid--title">
              <a class="text-truncate--line" 
                 href="${resourceUrl}" 
                 data-resource-card-link
                 id="resource-title-${index}"
                 aria-label="View ${resourceName}">
                ${resourceName}
              </a>
            </div>
          </div>
        </article>
      </div>
    `;
  }

  /**
   * Initializes pagination system and DOM references
   * @private
   * @description Sets up essential DOM element references, configures pagination
   * settings from data attributes, and adds accessibility attributes to the
   * grid container. Must be called before rendering resources.
   * @returns {void}
   */
  initializePagination() {
    this.gridContainer = document.querySelector('#ResourceGrid .resource-grid--wrapper');
    this.paginationContainer = document.getElementById('resource-pagination');
    
    // Add accessibility attributes to grid container
    if (this.gridContainer) {
      if (!this.gridContainer.getAttribute('role')) {
        this.gridContainer.setAttribute('role', 'region');
      }
      if (!this.gridContainer.getAttribute('aria-label') && !this.gridContainer.getAttribute('aria-labelledby')) {
        this.gridContainer.setAttribute('aria-label', 'Resource grid');
      }
    }
    
    // Get resources per page from data attribute
    const resourcesPerPageAttr = this.gridContainer?.getAttribute('data-resources-per-page');
    this.resourcesPerPage = resourcesPerPageAttr ? parseInt(resourcesPerPageAttr) : 16;
    
    //console.log('Pagination initialized with', this.resourcesPerPage, 'resources per page');
    //console.log('Grid container found:', !!this.gridContainer);
  }

  /**
   * Utility method to check if a value is a valid date
   * @param {*} value - Value to test for date validity
   * @description Checks if the provided value is either a Date object
   * or a string that can be parsed as a valid date.
   * @returns {boolean} True if value is a valid date, false otherwise
   * @example
   * this.isDate('2024-01-01'); // true
   * this.isDate(new Date());   // true
   * this.isDate('invalid');    // false
   */
  isDate(value) {
    return value instanceof Date || !isNaN(Date.parse(value));
  }
}

// Initialize the resource filter when the DOM is loaded
document.addEventListener('DOMContentLoaded', async () => {
  //console.log('DOM loaded, initializing ResourceFilter');
  window.resourceFilter = new ResourceFilter();
});